import {
    Alert, Box, Button, Card, CardActionArea, IconButton, LinearProgress, Snackbar,
    Step, StepLabel, Stepper, TextField, Typography, styled
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LinkIcon from '@mui/icons-material/Link';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CollectionsIcon from '@mui/icons-material/Collections';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { ImportedRecipe, ImportHandoff, ImportJob } from "../../../models/ImportedRecipe";
import {
    downscaleImage, getImportJob, ImportError, importFromUrl, startPhotoImport, toImageFile
} from "../../../utils/ImportService";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const MAX_PHOTOS = 3;
const POLL_INTERVAL_MS = 2000;
/** Three minutes at the poll interval above; past that the job is not coming back. */
const MAX_POLLS = 90;

type Mode = 'choose' | 'url' | 'photo';

/**
 * Maps a failure onto a translation key.
 *
 * The backend cannot send a message: it has no @ControllerAdvice, so Spring's default error body
 * carries only a status, and the text would have to be translated here anyway.
 */
function errorKeyForStatus(status: number): string {
    switch (status) {
        case 400: return 'importErrorInvalidUrl';
        case 415: return 'importErrorUnsupportedImage';
        case 422: return 'importErrorNoStructuredData';
        case 429: return 'importErrorBusy';
        case 502: return 'importErrorUnreachable';
        case 503: return 'importErrorNotConfigured';
        default: return 'importErrorGeneric';
    }
}

function errorKeyForCode(code: string | undefined): string {
    switch (code) {
        case 'NO_RECIPE_FOUND': return 'importErrorNoRecipeFound';
        case 'LLM_NOT_CONFIGURED': return 'importErrorNotConfigured';
        case 'LLM_FAILED': return 'importErrorLlm';
        case 'UNSUPPORTED_IMAGE': return 'importErrorUnsupportedImage';
        default: return 'importErrorGeneric';
    }
}

const RecipeImport = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [mode, setMode] = useState<Mode>('choose');
    const [url, setUrl] = useState<string>('');
    const [photos, setPhotos] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const [running, setRunning] = useState<boolean>(false);
    const [stage, setStage] = useState<string>('UPLOAD');
    const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>();
    /** Shown as a persistent alert rather than a snackbar: it needs the actions underneath it. */
    const [blockingErrorKey, setBlockingErrorKey] = useState<string | undefined>();

    const pollTimer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

    const stopPolling = useCallback(() => {
        if (pollTimer.current !== undefined) {
            clearInterval(pollTimer.current);
            pollTimer.current = undefined;
        }
    }, []);

    // Object URLs and the poll timer both outlive the render that created them.
    useEffect(() => {
        return () => {
            stopPolling();
            previews.forEach(preview => URL.revokeObjectURL(preview));
        };
    }, [stopPolling, previews]);

    /**
     * Hands the extracted recipe to the ordinary recipe editor, which doubles as the review screen.
     * The editor reads this from the router's location state; nothing has been saved yet.
     */
    const openReviewScreen = useCallback((imported: ImportedRecipe) => {
        const handoff = (image?: File) => {
            const state: ImportHandoff = {
                importedRecipe: imported.recipe,
                importedImage: image,
                warnings: imported.warnings
            };
            navigate('/recipe/edit/-1', { state });
        };

        if (imported.imageBase64) {
            toImageFile(imported.imageBase64, imported.imageMimeType)
                .then(handoff)
                .catch(() => handoff(undefined));
        } else {
            handoff(undefined);
        }
    }, [navigate]);

    const startUrlImport = useCallback(() => {
        if (!url.trim()) {
            return;
        }

        setRunning(true);
        setBlockingErrorKey(undefined);

        importFromUrl(url.trim())
            .then(openReviewScreen)
            .catch(error => {
                setRunning(false);
                const status: number = error instanceof ImportError ? error.status : 0;
                const key: string = errorKeyForStatus(status);
                // 422 is the expected miss for a site without structured data, and the user needs
                // somewhere to go from it - so it gets a persistent alert with actions, not a
                // snackbar that disappears after three seconds.
                if (status === 422) {
                    setBlockingErrorKey(key);
                } else {
                    setSnackbarMessage(t(key));
                }
            });
    }, [url, openReviewScreen, t]);

    const addPhotos = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const selected: File[] = Array.from(event.target.files ?? []);
        // Reset the input so picking the same file twice in a row still fires a change event.
        event.target.value = '';
        if (selected.length === 0) {
            return;
        }

        setPhotos(current => {
            const room: number = MAX_PHOTOS - current.length;
            if (room <= 0) {
                setSnackbarMessage(t('importErrorTooManyPhotos'));
                return current;
            }
            const accepted: File[] = selected.slice(0, room);
            if (selected.length > room) {
                setSnackbarMessage(t('importErrorTooManyPhotos'));
            }
            setPreviews(currentPreviews =>
                currentPreviews.concat(accepted.map(file => URL.createObjectURL(file))));
            return current.concat(accepted);
        });
    }, [t]);

    const removePhoto = useCallback((index: number) => {
        setPhotos(current => current.filter((_, i) => i !== index));
        setPreviews(current => {
            URL.revokeObjectURL(current[index]);
            return current.filter((_, i) => i !== index);
        });
    }, []);

    const failPhotoImport = useCallback((key: string) => {
        stopPolling();
        setRunning(false);
        setSnackbarMessage(t(key));
    }, [stopPolling, t]);

    const startPhotoImportRun = useCallback(() => {
        if (photos.length === 0) {
            return;
        }

        setRunning(true);
        setStage('UPLOAD');
        setBlockingErrorKey(undefined);

        Promise.all(photos.map(photo => downscaleImage(photo)))
            .then(startPhotoImport)
            .then(jobId => {
                setStage('READING');
                let polls = 0;

                pollTimer.current = setInterval(() => {
                    polls++;
                    if (polls > MAX_POLLS) {
                        failPhotoImport('importErrorGeneric');
                        return;
                    }

                    getImportJob(jobId).then((job: ImportJob) => {
                        if (job.status === 'RUNNING') {
                            setStage(job.stage);
                            return;
                        }

                        stopPolling();
                        if (job.status === 'DONE' && job.result) {
                            openReviewScreen(job.result);
                        } else {
                            setRunning(false);
                            setSnackbarMessage(t(errorKeyForCode(job.errorCode)));
                        }
                    }).catch(() => {
                        // A single dropped poll is not a failure; the next tick retries. Only the
                        // poll budget above ends the wait.
                    });
                }, POLL_INTERVAL_MS);
            })
            .catch(error => {
                setRunning(false);
                setSnackbarMessage(t(errorKeyForStatus(error instanceof ImportError ? error.status : 0)));
            });
    }, [photos, stopPolling, failPhotoImport, openReviewScreen, t]);

    const goBack = useCallback(() => {
        stopPolling();
        setMode('choose');
        setRunning(false);
        setBlockingErrorKey(undefined);
    }, [stopPolling]);

    const activeStep: number = stage === 'UPLOAD' ? 0 : 1;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, gap: 2 }}>
            <Box sx={{ width: '100%', maxWidth: 600 }}>

                {mode === 'choose' && (
                    <>
                        <Typography variant="h6" sx={{ mb: 2 }}>{t('importRecipe')}</Typography>

                        <Card sx={{ mb: 2 }}>
                            <CardActionArea onClick={() => setMode('url')} sx={{ p: 3, display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                                <LinkIcon color="primary" fontSize="large" sx={{ flexShrink: 0 }} />
                                <Box sx={{ textAlign: 'left' }}>
                                    <Typography variant="subtitle1" fontWeight={600}>{t('importFromWebsite')}</Typography>
                                    <Typography variant="body2" color="text.secondary">{t('importFromWebsiteHint')}</Typography>
                                </Box>
                            </CardActionArea>
                        </Card>

                        <Card>
                            <CardActionArea onClick={() => setMode('photo')} sx={{ p: 3, display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                                <PhotoCameraIcon color="primary" fontSize="large" sx={{ flexShrink: 0 }} />
                                <Box sx={{ textAlign: 'left' }}>
                                    <Typography variant="subtitle1" fontWeight={600}>{t('importFromPhoto')}</Typography>
                                    <Typography variant="body2" color="text.secondary">{t('importFromPhotoHint')}</Typography>
                                </Box>
                            </CardActionArea>
                        </Card>
                    </>
                )}

                {mode === 'url' && (
                    <>
                        <Button startIcon={<ArrowBackIcon />} onClick={goBack} disabled={running} sx={{ mb: 1 }}>
                            {t('back')}
                        </Button>
                        <Typography variant="h6" sx={{ mb: 2 }}>{t('importFromWebsite')}</Typography>

                        <TextField
                            autoFocus
                            fullWidth
                            type="url"
                            inputMode="url"
                            label={t('importUrlLabel')}
                            helperText={t('importUrlHelper')}
                            value={url}
                            disabled={running}
                            onChange={(e) => setUrl(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { startUrlImport(); } }}
                        />

                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ mt: 2 }}
                            disabled={running || !url.trim()}
                            onClick={startUrlImport}
                        >
                            {running ? t('importing') : t('importStart')}
                        </Button>

                        {running && <LinearProgress sx={{ mt: 2 }} />}

                        {blockingErrorKey && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>{t(blockingErrorKey)}</Typography>
                                <Button size="small" onClick={() => setBlockingErrorKey(undefined)}>
                                    {t('importTryAnotherLink')}
                                </Button>
                                <Button size="small" startIcon={<EditNoteIcon />}
                                        onClick={() => navigate('/recipe/edit/-1')}>
                                    {t('importEnterManually')}
                                </Button>
                            </Alert>
                        )}
                    </>
                )}

                {mode === 'photo' && (
                    <>
                        <Button startIcon={<ArrowBackIcon />} onClick={goBack} disabled={running} sx={{ mb: 1 }}>
                            {t('back')}
                        </Button>
                        <Typography variant="h6" sx={{ mb: 2 }}>{t('importFromPhoto')}</Typography>

                        {!running && (
                            <>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    {/* Two inputs rather than one: "capture" opens the camera directly on a
                                        phone but is ignored or unhelpful on a desktop, so the file picker
                                        stays available alongside it. */}
                                    <Button component="label" variant="outlined" startIcon={<PhotoCameraIcon />} fullWidth>
                                        {t('importTakePhoto')}
                                        <VisuallyHiddenInput type="file" accept="image/*" capture="environment"
                                                             multiple onChange={addPhotos} />
                                    </Button>
                                    <Button component="label" variant="outlined" startIcon={<CollectionsIcon />} fullWidth>
                                        {t('importChooseFile')}
                                        <VisuallyHiddenInput type="file" accept="image/*" multiple onChange={addPhotos} />
                                    </Button>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                    {previews.map((preview, index) => (
                                        <Box key={preview} sx={{ position: 'relative' }}>
                                            <img src={preview} alt="" style={{
                                                width: 100, height: 100, objectFit: 'cover', borderRadius: 8
                                            }} />
                                            <IconButton
                                                size="small"
                                                aria-label={t('importRemovePhoto')}
                                                onClick={() => removePhoto(index)}
                                                sx={{
                                                    position: 'absolute', top: 2, right: 2,
                                                    backgroundColor: 'rgba(0,0,0,0.5)', color: 'white',
                                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                                                }}
                                            >
                                                <DeleteIcon fontSize="inherit" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                    {t('importPhotoHint')}
                                </Typography>

                                <Button variant="contained" fullWidth sx={{ mt: 2 }}
                                        disabled={photos.length === 0} onClick={startPhotoImportRun}>
                                    {t('importStart')}
                                </Button>
                            </>
                        )}

                        {running && (
                            <Box sx={{ mt: 2 }}>
                                {/* A blank spinner for forty seconds reads as a hang; naming the step
                                    the server is on reads as progress. */}
                                <Stepper activeStep={activeStep} alternativeLabel>
                                    <Step key="upload"><StepLabel>{t('importStepUpload')}</StepLabel></Step>
                                    <Step key="read"><StepLabel>{t('importStepRead')}</StepLabel></Step>
                                </Stepper>
                                <LinearProgress sx={{ mt: 3 }} />
                                <Typography variant="caption" color="text.secondary"
                                            sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                                    {t('importEstimatedTime')}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackbarMessage !== undefined}
                autoHideDuration={5000}
                onClose={() => setSnackbarMessage(undefined)}
            >
                <Alert onClose={() => setSnackbarMessage(undefined)} severity="error" variant="filled"
                       sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default RecipeImport;
